// src/App.jsx
/**
 * Application entry point.
 *
 * Responsibilities
 * ────────────────
 *  • Mount the two possible nav variants (`<NavBar/> | <MDNavBar/>`).
 *  • Gate protected routes behind `<ProtectedRoute/>`.
 *  • Lazy-load page modules to keep initial bundle size low.
 *  • Redirect unmatched paths to `/`.
 *  • Run a quick device-capability check (`useDeviceCompatibility`). On
 *    unsupported devices we replace the `/auth/*` subtree with a
 *    compatibility-warning page.
 *
 * Layout skeleton
 *  <div class="h-[100dvh] flex flex-col">   ← fills viewport height
 *    {NavBar}
 *    <Suspense>  ← lazy pages rendered inside
 *       <Routes> … </Routes>
 *    </Suspense>
 *  </div>
 *
 * Notes
 *  • Use `100dvh` (dynamic viewport unit) so Safari iOS 15+ doesn’t crop
 *    the content when the URL bar shrinks.
 *  • `typography` Tailwind plugin class gives consistent heading / paragraph
 *    defaults across pages.
 */

import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

/* ---------------------------------------- FIREBASE */
import { firebaseApp } from "./firebase/firebaseApp";
import { getAuth, signOut } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFirestore } from "firebase/firestore";

/* ---------------------------------------- PAGES */
import HomePage from "./pages/HomePage";
import ToDoPage from "./pages/ToDoPage";
import FormPage from "./pages/FormPage";
import QuestionnairesPage from "./pages/QuestionnairesPage";
import ConfirmMetadataPage from "./pages/ConfirmMetadataPage";
import NoticePage from "./pages/NoticePage";
import AboutPage from "./pages/AboutPage";
import ResultsPage from "./pages/ResultsPage";
import ResultPage from "./pages/ResultPage";
import GuidancePage from "./pages/GuidancePage";
import GeneralGuidancePage from "./pages/GeneralGuidancePage";
import SpirometryGuidancePage from "./pages/SpirometryGuidancePage";
import CardiopulmonaryGuidancePage from "./pages/CardiopulmonaryGuidancePage";
import SixMinuteWalkFlowPage from "./pages/SixMinuteWalkFlowPage";
/* ---------------------------------------- COMPONENTS */
import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";
import { CompatibleRoute } from "./components/navigation/CompatibleRoute";
import GlobalUploadToast from "./components/common/GlobalUploadToast";
import CookieConsentBanner from "./components/consent/CookieConsentBanner";
import AnalyticsGate from "./components/consent/AnalyticsGate";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";
import HomePageLoadingShell from "./components/pageLoading/HomePageLoadingShell";
import ProfilePageLoadingShell from "./components/pageLoading/ProfilePageLoadingShell";
import LegalDocumentPage from "./components/legal/LegalDocumentPage";
import LegalUpdateOverlay from "./components/legal/LegalUpdateOverlay";

/* ---------------------------------------- CONTEXT */
import { useConsent } from "./context/ConsentContext";
import { useAuth } from "./context/AuthContext";
import { useFirstRun } from "./context/FirstRunContext";
import { useProfilePrefetch } from "./context/ProfilePrefetchContext";
import { useRegionalBackend } from "./context/RegionalBackendContext";
import { UserClinicMappingProvider } from "./context/UserClinicMappingContext";

/* ---------------------------------------- UTILS */
import { useSyncHtmlLang } from "./utils/useSyncHtmlLang";
import { useNavVariant } from "./utils/navigation/useNavVariant";
import { analytics, trackPageView } from "./utils/analytics/track";
import { MEASUREMENT_VARIANTS, QUESTIONNAIRES } from "./utils/constants";
import { RECORDING_MODES } from "./utils/recordingModes";

/* ---------------------------------------- HOOKS */
import useFirstRunLoginPrefetch from "./hooks/useFirstRunLoginPrefetch";
import useAuthCacheHygiene from "./hooks/useAuthCacheHygiene";
import useProfileLoginPrefetch from "./hooks/useProfileLoginPrefetch";
import useRegionalBootstrapWarmup from "./hooks/useRegionalBootstrapWarmup";
import useProtocolTaskBoard, {
  getRecordingTaskIdForVariant,
} from "./hooks/useProtocolTaskBoard";
import {
  applyOptimisticTaskCompletions,
  applyRecordingUploadStatus,
  recordOptimisticTaskCompletion,
} from "./utils/tasks/optimisticTaskCompletion";
import { resolvePostQuestionnaireDestination } from "./utils/tasks/postQuestionnaireDestination";
import useUploadToasts from "./hooks/useUploadToasts";
import useBrowserCompatibility from "./hooks/useBrowserCompatibility";
import { useRecordingModeConfig } from "./hooks/useRecordingModeConfig";
import { useGlobalEntryLocale } from "./hooks/useGlobalEntryLocale";
import { usePresentationLabelling } from "./hooks/usePresentationLabelling";

/* ---------------------------------------- ROUTES */
import RecordingSpirometryRouter from "./routes/RecordingSpirometryRouter";
import RecordingTidalBreathingRouter from "./routes/RecordingTidalBreathingRouter";
import RecordingModeGate from "./components/recording/RecordingModeGate";
import RecordingManualAudioRouter from "./routes/RecordingManualAudioRouter";
import { useUserClinicMapping } from "./hooks/useUserClinicMapping";
import { getGuidePathFromCameraPath } from "./utils/navigation/recordingGuideNav";
import { useLastSafeRoute } from "./hooks/useLastSafeRoute";
import { useScrollToTopOnNavigate } from "./hooks/useScrollToTopOnNavigate";
import { APP_SCROLL_CONTAINER_ID } from "./utils/navigation/appScroll";
import { buildStudyAccessPath } from "./utils/navigation/onboardingPaths";
import { installGlobalSpeechPrimer } from "./utils/audio/speechPrimer";
import { clearStoredSixMinuteWalkAssessment } from "./utils/recording/sixMinuteWalkStorage";
import BottomNavBar from "./components/navigation/BottomNavBar";
import { MobileMeasureOverlay } from "./components/navigation/MobileMeasureOverlay";
import { MobileMenuOverlay } from "./components/navigation/MobileMenuOverlay";
import { MeasurePanel } from "./components/navigation/MeasurePanel";
import { MenuPanel } from "./components/navigation/MenuPanel";
import { isProcessingAllowed } from "./utils/legal/legalStatus";
import {
  completeRegionalMeasurementUploadSession,
  createRegionalMeasurementUploadSession as createRegionalMeasurementUploadSessionRequest,
  failRegionalMeasurementUploadSession,
} from "./utils/backend/pdxBackendClient";
import { captureCurrentEnvironmentalContext } from "./utils/environmentalContext";
import {
  filterRecordingModesForProductPresentationProfile,
  resolveDefaultRecordingModesForProductPresentationProfile,
} from "./utils/productPresentation/recordingModes";

/* ------------------------------------------------------------------ */
/* Lazy-loaded pages (code-splitting)                                 */
/* ------------------------------------------------------------------ */
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const StudyAccessPage = lazy(() => import("./pages/StudyAccessPage"));
const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const BrowserCompatibilityPage = lazy(
  () => import("./pages/BrowserCompatibilityPage"),
);

const DEFAULT_PERMITTED_QUESTIONNAIRES = ["sgrq"];

/* ------------------------------------------------------------------ */

function isStandaloneDisplayMode() {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.matchMedia?.("(display-mode: standalone)")?.matches) {
    return true;
  }

  if (window.matchMedia?.("(display-mode: fullscreen)")?.matches) {
    return true;
  }

  return window.navigator?.standalone === true;
}

export default function App() {
  const auth = getAuth(firebaseApp);
  const storage = getStorage(firebaseApp);
  const db = getFirestore(firebaseApp);
  const { appName } = usePresentationLabelling("userWebApp");
  const { user } = useAuth();
  const {
    contextMetadata,
    contextVersion,
    legalStatus,
    productPresentationProfile,
  } = useRegionalBackend();
  const [activeNavSheet, setActiveNavSheet] = useState(null);
  const [taskBoardRefreshKey, setTaskBoardRefreshKey] = useState(0);
  const [optimisticTaskCompletions, setOptimisticTaskCompletions] = useState(
    {},
  );
  // Spoken-instruction mute, owned here so the navbar control and the recording
  // pod share one value and it survives guide <-> camera navigation. Audio-only:
  // the pod always renders its on-screen instruction text.
  const [audioMuted, setAudioMuted] = useState(false);
  const navigate = useNavigate();
  const menuOpen = activeNavSheet === "menu";
  const measureOpen = activeNavSheet === "measure";
  const closeNavSheets = useCallback(() => {
    setActiveNavSheet(null);
  }, []);
  const handleToggleMenu = useCallback(() => {
    setActiveNavSheet((sheet) => (sheet === "menu" ? null : "menu"));
  }, []);
  const handleToggleMeasure = useCallback(() => {
    setActiveNavSheet((sheet) => (sheet === "measure" ? null : "measure"));
  }, []);
  const createMeasurementUploadSession = useCallback(
    async (payload, options = {}) => {
      let environmentalContext = null;

      try {
        environmentalContext = await captureCurrentEnvironmentalContext({
          purpose: "measurement",
        });
      } catch (error) {
        console.warn("Environmental context lookup failed:", error);
      }

      return createRegionalMeasurementUploadSessionRequest(
        environmentalContext
          ? { ...payload, environmentalContext }
          : payload,
        options,
      );
    },
    [],
  );

  useGlobalEntryLocale();

  useEffect(() => installGlobalSpeechPrimer(), []);

  // Decide which nav bar variant to render based on location auth state.
  const navVariant = useNavVariant(); // 'main' | 'landing'

  useEffect(() => {
    if (typeof document === "undefined" || !appName) {
      return;
    }

    document.title = appName;
  }, [appName]);

  const {
    ensureProfile,
    ensureProfileInfo,
    getCachedProfileInfo,
    clearProfile,
    clearAllProfiles,
  } = useProfilePrefetch();

  useProfileLoginPrefetch({
    uid: user?.uid,
    ensureProfileInfo,
    ensureProfile,
    getCachedProfileInfo,
    ttlMs: 10 * 60 * 1000,
    recertMs: 3 * 30 * 24 * 60 * 60 * 1000,
  });

  const mapping = useUserClinicMapping({ uid: user?.uid });

  const {
    loading: loadingModeConfig,
    mappingResolved,
    clinicStudyId,
    config,
    error: recordingModeConfigError,
  } = useRecordingModeConfig({ uid: user?.uid, mapping });

  const isClinicScoped = Boolean(clinicStudyId);
  const configuredPermittedModes = Array.isArray(config?.permittedModes)
    ? config.permittedModes
    : [];
  const permittedModes = useMemo(
    () =>
      filterRecordingModesForProductPresentationProfile(
        configuredPermittedModes,
        productPresentationProfile,
      ),
    [configuredPermittedModes, productPresentationProfile],
  );
  const defaultPermittedModes = useMemo(
    () =>
      resolveDefaultRecordingModesForProductPresentationProfile(
        productPresentationProfile,
      ),
    [productPresentationProfile],
  );
  const configuredPermittedQuestionnaires = Array.isArray(
    config?.permittedQuestionnaires,
  )
    ? config.permittedQuestionnaires
    : [];
  const knownQuestionnaireSlugs = useMemo(
    () => QUESTIONNAIRES.map(({ slug }) => slug),
    [],
  );

  const modesReady = mappingResolved && !loadingModeConfig;
  const recordingAccessError =
    isClinicScoped && modesReady && !config
      ? recordingModeConfigError || "not_found"
      : null;

  const isPermitted = (mode) => {
    if (!modesReady) return false;

    if (isClinicScoped) {
      return permittedModes.includes(mode);
    }

    if (defaultPermittedModes.includes(mode)) return true;

    return false;
  };

  const permittedQuestionnaires = useMemo(() => {
    if (!modesReady) return [];

    if (isClinicScoped) {
      return knownQuestionnaireSlugs.filter((slug) =>
        configuredPermittedQuestionnaires.includes(slug),
      );
    }

    return knownQuestionnaireSlugs.filter((slug) =>
      DEFAULT_PERMITTED_QUESTIONNAIRES.includes(slug),
    );
  }, [
    modesReady,
    isClinicScoped,
    configuredPermittedQuestionnaires,
    knownQuestionnaireSlugs,
  ]);
  const warmupPermittedModes = useMemo(() => {
    if (!modesReady) return [];

    if (isClinicScoped) {
      return permittedModes;
    }

    return defaultPermittedModes;
  }, [defaultPermittedModes, isClinicScoped, modesReady, permittedModes]);

  const isQuestionnairePermitted = useCallback(
    (slug) => permittedQuestionnaires.includes(slug),
    [permittedQuestionnaires],
  );
  // Rerun the task-board query when the verified backend context settles after
  // onboarding/profile writes, not only when a task itself changes in-app.
  const protocolTaskBoardRefreshKey = `${contextVersion}:${taskBoardRefreshKey}`;

  const { ensureFirstRun, getCachedFirstRun, clearAllFirstRun } = useFirstRun();

  useFirstRunLoginPrefetch({
    uid: user?.uid,
    db,
    ensureFirstRun,
    variants: MEASUREMENT_VARIANTS,
    ttlMs: 10 * 60 * 1000,
    // requireCompleted: true, // enable if you store status == "completed"
  });

  const firstRunEntry = user?.uid ? getCachedFirstRun(user.uid) : null;
  // While the weekly reminder check is loading, default to skipping the guide.
  // Once ready, home tiles will use the real per-variant reminder value.
  const firstRunByVariant =
    firstRunEntry?.status === "ready" && firstRunEntry?.value?.byVariant
      ? firstRunEntry.value.byVariant
      : Object.fromEntries(MEASUREMENT_VARIANTS.map((v) => [v, false]));
  const fetchedProtocolTaskBoard = useProtocolTaskBoard({
    uid: user?.uid,
    firstRunByVariant,
    loading: !modesReady,
    refreshKey: protocolTaskBoardRefreshKey,
    accountCreatedAt: contextMetadata?.createdAt,
  });
  // A recording is reflected locally as soon as its upload starts; the refetch
  // below reconciles once the backend's projection catches up.
  const protocolTaskBoard = useMemo(
    () =>
      applyOptimisticTaskCompletions(
        fetchedProtocolTaskBoard,
        optimisticTaskCompletions,
      ),
    [fetchedProtocolTaskBoard, optimisticTaskCompletions],
  );

  // Both boards are held in refs so the handlers below stay stable: the fetched
  // one is what a new optimistic floor counts up from, while the overlaid one is
  // what the user is actually looking at.
  const fetchedProtocolTaskBoardRef = useRef(fetchedProtocolTaskBoard);
  const protocolTaskBoardRef = useRef(protocolTaskBoard);
  useEffect(() => {
    fetchedProtocolTaskBoardRef.current = fetchedProtocolTaskBoard;
    protocolTaskBoardRef.current = protocolTaskBoard;
  }, [fetchedProtocolTaskBoard, protocolTaskBoard]);

  const handleRecordingCompleted = useCallback(
    ({ variant, recordingId } = {}) => {
      const taskId = getRecordingTaskIdForVariant(variant);

      if (taskId) {
        setOptimisticTaskCompletions((previous) =>
          recordOptimisticTaskCompletion(
            previous,
            taskId,
            fetchedProtocolTaskBoardRef.current,
            // The same id the pod reported the upload under, so a task already
            // ticked off when the upload started is not counted twice here.
            { sourceId: recordingId ?? null },
          ),
        );
      }

      setTaskBoardRefreshKey((previousKey) => previousKey + 1);
    },
    [],
  );

  /**
   * Refetches the board and answers where the finished questionnaire leads —
   * the To do list while anything is still outstanding, Home once it is clear.
   */
  const handleQuestionnaireSubmitted = useCallback((questionnaireSlug) => {
    setTaskBoardRefreshKey((previousKey) => previousKey + 1);

    return resolvePostQuestionnaireDestination(
      protocolTaskBoardRef.current,
      questionnaireSlug ?? null,
    );
  }, []);

  const questionnaireTaskBoard = useMemo(
    () => ({
      ...protocolTaskBoard,
      tasks: protocolTaskBoard.questionnaireTasks,
      summary: {
        ...protocolTaskBoard.summary,
        assignedTasksCount: protocolTaskBoard.questionnaireTasks.length,
        completedTasksCount: protocolTaskBoard.questionnaireTasks.filter(
          (task) => task.state === "completed",
        ).length,
        dueTasksCount: protocolTaskBoard.questionnaireTasks.filter(
          (task) => task.state === "due",
        ).length,
        overdueTasksCount: protocolTaskBoard.questionnaireTasks.filter(
          (task) => task.state === "overdue",
        ).length,
      },
    }),
    [protocolTaskBoard],
  );

  useAuthCacheHygiene({
    uid: user?.uid,
    clearProfile,
    clearAllProfiles,
    clearAllFirstRun,
  });

  useRegionalBootstrapWarmup({
    db,
    uid: user?.uid,
    ready: modesReady,
    recordingAccessError,
    permittedModes: warmupPermittedModes,
    permittedQuestionnaires,
  });

  const handleSignOut = useCallback(() => {
    try {
      if (clearAllProfiles) clearAllProfiles();
      else if (user?.uid) clearProfile(user.uid);
    } catch {
      // ignore
    }

    signOut(getAuth(firebaseApp))
      .then(() => navigate("/auth/login", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err),
      );
  }, [navigate, clearAllProfiles, clearProfile, user?.uid]);

  useSyncHtmlLang();
  const params = new URLSearchParams(window.location.search);
  const forceCompat = params.get("forceCompat"); // e.g. in_app_browser | webview | safari_webkit | any

  const detectedBrowserInfo = useBrowserCompatibility(); // always call hook

  const forcedInfo = forceCompat
    ? {
        isSupported: false,
        reasons: [forceCompat],
        browser: "Forced",
        context: "forced",
      }
    : null;

  const browserInfo = forcedInfo || detectedBrowserInfo;

  // Enforce on deep-link sensitive entry points (QR / recording flows)
  const pathname = window.location.pathname || "";
  const isSensitiveEntry =
    pathname.startsWith("/recording") || pathname.startsWith("/auth");

  const shouldShowBrowserWarning =
    isSensitiveEntry &&
    !browserInfo.isSupported &&
    !pathname.startsWith("/compatibility"); // avoid loops

  const {
    toastModel,
    hasActiveUploads,
    onUploadStatus: reportUploadStatusToToast,
    onUploadProgress,
  } = useUploadToasts();

  useEffect(() => {
    if (!hasActiveUploads || typeof window === "undefined") {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasActiveUploads]);

  /**
   * The pod reports "uploading" the moment a capture ends and only reaches
   * "success" once every byte is up — minutes later on a phone connection. The
   * To do list follows the first report, so it stops sitting outstanding with
   * the recording already taken, and an upload that fails releases it again.
   */
  const onUploadStatus = useCallback(
    (report) => {
      reportUploadStatusToToast(report);

      if (report?.suppressTaskCompletion === true) {
        return;
      }

      setOptimisticTaskCompletions((previous) =>
        applyRecordingUploadStatus(
          previous,
          {
            taskId: getRecordingTaskIdForVariant(report?.variant),
            uploadId: report?.uploadId ?? null,
            status: report?.status,
          },
          fetchedProtocolTaskBoardRef.current,
        ),
      );
    },
    [reportUploadStatusToToast],
  );

  const location = useLocation();
  // The routed pages share one scroll container, so navigating between them
  // has to reset it explicitly — see useScrollToTopOnNavigate.
  useScrollToTopOnNavigate();
  const consent = useConsent();
  const analyticsAllowed = isProcessingAllowed(legalStatus, "analytics");

  useEffect(() => {
    if (consent?.status === "set" && consent?.analytics && analyticsAllowed) {
      trackPageView(location.pathname + location.search);
    }
  }, [
    analyticsAllowed,
    location.pathname,
    location.search,
    consent?.status,
    consent?.analytics,
  ]);

  const guidePath = useMemo(() => {
    return getGuidePathFromCameraPath(location?.pathname || "");
  }, [location?.pathname]);

  // Offer the mute control anywhere the current route can trigger spoken
  // instructions. For spirometry that means the whole guide + camera flow so the
  // user can decide before they place both hands on their chest and abdomen. The
  // six-minute-walk flow is host-rendered rather than a pod, but its live walk
  // step still speaks the turn-around prompt and benefits from the same navbar
  // control.
  const showAudioMute = useMemo(() => {
    const pathname = location?.pathname || "";

    return (
      pathname.startsWith("/recording/spirometry/") ||
      /^\/recording\/six-minute-walk\/walk\/?$/.test(pathname)
    );
  }, [location?.pathname]);

  const handleToggleAudioMuted = useCallback(() => {
    setAudioMuted((muted) => !muted);
  }, []);

  const handleAbandonSixMinuteWalkFlow = useCallback(() => {
    clearStoredSixMinuteWalkAssessment(user?.uid);
  }, [user?.uid]);

  useEffect(() => {
    closeNavSheets();
  }, [closeNavSheets, location.pathname, location.search]);

  const path = location?.pathname || "";
  const isSixMinuteWalkFlowRoute =
    path === "/recording/six-minute-walk" ||
    path.startsWith("/recording/six-minute-walk/");
  const isSixMinuteWalkPostRecordingRoute = /^\/recording\/six-minute-walk\/post(?:\/|$)/.test(
    path,
  );
  const isRecordingRoute = path.startsWith("/recording/");
  const isFormRoute =
    path.startsWith("/form") || path.startsWith("/study-access");
  const currentInternalPath = `${location.pathname}${location.search}`;
  const shouldOpenHomeOnStandaloneLaunch =
    path === "/" && Boolean(user) && isStandaloneDisplayMode();
  const spirometryVideoRetentionMode =
    config?.spirometryVideoRetentionMode || "full-frame";
  const recordingAccessRedirect = useMemo(() => {
    if (!mapping?.subjectId) {
      return buildStudyAccessPath({
        search: location.search,
        next: currentInternalPath,
      });
    }

    return "/home";
  }, [currentInternalPath, location.search, mapping?.subjectId]);

  const flowExitControl = useMemo(() => {
    if (!isSixMinuteWalkFlowRoute) {
      return null;
    }

    if (!isSixMinuteWalkPostRecordingRoute) {
      return null;
    }

    return {
      onConfirm: handleAbandonSixMinuteWalkFlow,
    };
  }, [
    handleAbandonSixMinuteWalkFlow,
    isSixMinuteWalkFlowRoute,
    isSixMinuteWalkPostRecordingRoute,
  ]);

  useLastSafeRoute();

  const tidalRecordingDurationSeconds = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("recordingDurationSeconds");

    if (raw == null || raw.trim() === "") return 60;

    const parsed = Number(raw);

    if (!Number.isFinite(parsed)) return 60;

    return parsed;
  }, [location.search]);

  return (
    <UserClinicMappingProvider value={mapping}>
      <div className="h-[100dvh] flex flex-col typography">
        {navVariant === "main" ? (
          <NavBar
            menuOpen={menuOpen}
            measureOpen={measureOpen}
            onToggleMeasure={handleToggleMeasure}
            onToggleMenu={handleToggleMenu}
            onCloseMenu={closeNavSheets}
            guidePath={guidePath}
            showAudioMute={showAudioMute}
            audioMuted={audioMuted}
            onToggleAudioMuted={handleToggleAudioMuted}
            flowExitControl={flowExitControl}
            handleSignOut={handleSignOut}
          />
        ) : (
          <MDNavBar />
        )}

        {/* Consent banner (shown only when status !== 'set') */}
        <CookieConsentBanner />

        <LegalUpdateOverlay onExit={handleSignOut} />

        {/* Loads analytics only after analytics consent */}
        <AnalyticsGate />

        <GlobalUploadToast toastModel={toastModel} />

        {/* Suspense boundary – shows fallback while lazy chunks download */}
        <div
          id={APP_SCROLL_CONTAINER_ID}
          className="flex flex-col items-center justify-start h-full overflow-y-scroll"
        >
          <Suspense fallback={<div className="m-auto">Loading…</div>}>
            {shouldShowBrowserWarning ? (
              <BrowserCompatibilityPage info={browserInfo} />
            ) : (
              <Routes>
                {/* ---------- Public routes ---------- */}
                <Route
                  path="/"
                  element={
                    shouldOpenHomeOnStandaloneLaunch ? (
                      <Navigate to="/home" replace />
                    ) : (
                      <LandingPage />
                    )
                  }
                />
                <Route
                  path="/auth/*"
                  // element={<AuthPage />}
                  element={<AuthPage />}
                />
                <Route
                  path="/auth/forgot-password"
                  element={<ForgotPasswordRequest />}
                />
                <Route
                  path="/auth/reset-password"
                  element={<ResetPassword />}
                />
                <Route
                  path="/legal/terms"
                  element={<LegalDocumentPage documentType="terms" />}
                />
                <Route
                  path="/legal/privacy"
                  element={<LegalDocumentPage documentType="privacy" />}
                />
                <Route path="/about" element={<AboutPage />} />
                {/* ---------- Protected routes ---------- */}
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute loadingFallback={<HomePageLoadingShell />}>
                      <HomePage
                        db={db}
                        user={user}
                        firstRunByVariant={firstRunByVariant}
                        isPermitted={isPermitted}
                        taskBoard={protocolTaskBoard}
                        loadingModes={!modesReady}
                        historyRefreshKey={taskBoardRefreshKey}
                        isClinicScoped={isClinicScoped}
                        recordingAccessError={recordingAccessError}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/todo"
                  element={
                    <ProtectedRoute>
                      <ToDoPage
                        taskBoard={protocolTaskBoard}
                        loadingModes={!modesReady}
                        accountCreatedAt={contextMetadata?.createdAt}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questionnaires/:questionnaireName?"
                  element={
                    <ProtectedRoute>
                      <QuestionnairesPage
                        uid={user?.uid}
                        permittedQuestionnaires={permittedQuestionnaires}
                        isQuestionnairePermitted={isQuestionnairePermitted}
                        loadingQuestionnaires={!modesReady}
                        recordingAccessError={recordingAccessError}
                        taskBoard={questionnaireTaskBoard}
                        historyRefreshKey={taskBoardRefreshKey}
                        onQuestionnaireSubmitted={handleQuestionnaireSubmitted}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/notice?"
                  element={
                    <ProtectedRoute>
                      <NoticePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/results"
                  element={
                    <ProtectedRoute>
                      <ResultsPage
                        user={user}
                        isPermitted={isPermitted}
                        loadingModes={!modesReady}
                        firstRunByVariant={firstRunByVariant}
                        recordingAccessError={recordingAccessError}
                        taskBoard={protocolTaskBoard}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/result"
                  element={
                    <ProtectedRoute>
                      <ResultPage
                        user={user}
                        isPermitted={isPermitted}
                        loadingModes={!modesReady}
                        firstRunByVariant={firstRunByVariant}
                        recordingAccessError={recordingAccessError}
                        taskBoard={protocolTaskBoard}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance"
                  element={
                    <ProtectedRoute>
                      <GuidancePage isPermitted={isPermitted} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance/general"
                  element={
                    <ProtectedRoute>
                      <GeneralGuidancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance/spirometry"
                  element={
                    <ProtectedRoute>
                      <SpirometryGuidancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance/cardiopulmonary"
                  element={
                    <ProtectedRoute>
                      <CardiopulmonaryGuidancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/study-access"
                  element={
                    <ProtectedRoute allowMissingStudyAccess>
                      <StudyAccessPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/form"
                  element={
                    <ProtectedRoute allowMissingMetadata>
                      <FormPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/about" element={<AboutPage />} />

                <Route
                  path="/recording"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <Navigate to="/" replace />
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/spirometry/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("spirometry")}
                          loading={!modesReady}
                          redirectTo={recordingAccessRedirect}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingSpirometryRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            firstRun={
                              // true
                              !!firstRunByVariant["RecordingSpirometry"]
                            }
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            onComplete={handleRecordingCompleted}
                            createMeasurementUploadSession={
                              createMeasurementUploadSession
                            }
                            completeMeasurementUploadSession={
                              completeRegionalMeasurementUploadSession
                            }
                            failMeasurementUploadSession={
                              failRegionalMeasurementUploadSession
                            }
                            analytics={analytics}
                            appMode={"spirometry"}
                            subjectId={mapping?.subjectId}
                            showTrackedPoints={false}
                            poseDebug={false}
                            traceMode={"static"}
                            maxTrackedPoints={50}
                            includePreExhale={true}
                            audioMuted={audioMuted}
                            videoRetentionMode={spirometryVideoRetentionMode}
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/tidal-breathing/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("tidal-breathing")}
                          loading={!modesReady}
                          redirectTo={recordingAccessRedirect}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingTidalBreathingRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            firstRun={
                              !!firstRunByVariant["RecordingTidalBreathing"]
                            }
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            onComplete={handleRecordingCompleted}
                            createMeasurementUploadSession={
                              createMeasurementUploadSession
                            }
                            completeMeasurementUploadSession={
                              completeRegionalMeasurementUploadSession
                            }
                            failMeasurementUploadSession={
                              failRegionalMeasurementUploadSession
                            }
                            analytics={analytics}
                            variant={"RecordingTidalBreathing"}
                            showLiveChart={false}
                            appMode={"respiratory"}
                            subjectId={mapping?.subjectId}
                            enablePixelSignal={false}
                            enableGrid3x3Pixel={false}
                            recordingDurationSeconds={
                              tidalRecordingDurationSeconds
                            }
                            basePath="/recording/tidal-breathing"
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/post-exertion/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("post-exertion")}
                          loading={!modesReady}
                          redirectTo={recordingAccessRedirect}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingTidalBreathingRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            onComplete={handleRecordingCompleted}
                            createMeasurementUploadSession={
                              createMeasurementUploadSession
                            }
                            completeMeasurementUploadSession={
                              completeRegionalMeasurementUploadSession
                            }
                            failMeasurementUploadSession={
                              failRegionalMeasurementUploadSession
                            }
                            analytics={analytics}
                            showLiveChart={false}
                            appMode={"post-exertion"}
                            subjectId={mapping?.subjectId}
                            enablePixelSignal={false}
                            enableGrid3x3Pixel={false}
                            firstRun={
                              !!firstRunByVariant["RecordingPostExertion"]
                            }
                            variant={"RecordingPostExertion"}
                            recordingDurationSeconds={180}
                            basePath="/recording/post-exertion"
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/six-minute-walk/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted(RECORDING_MODES.SIX_MINUTE_WALK)}
                          loading={!modesReady}
                          redirectTo={recordingAccessRedirect}
                          defaultMode={config?.defaultMode}
                        >
                          <SixMinuteWalkFlowPage
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            firstRun={
                              !!firstRunByVariant["RecordingSixMinuteWalk"]
                            }
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            onComplete={handleRecordingCompleted}
                            createMeasurementUploadSession={
                              createMeasurementUploadSession
                            }
                            completeMeasurementUploadSession={
                              completeRegionalMeasurementUploadSession
                            }
                            failMeasurementUploadSession={
                              failRegionalMeasurementUploadSession
                            }
                            analytics={analytics}
                            subjectId={mapping?.subjectId}
                            audioMuted={audioMuted}
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/manual-audio/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("manual-audio")}
                          loading={!modesReady}
                          redirectTo={recordingAccessRedirect}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingManualAudioRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            firstRun={
                              !!firstRunByVariant["RecordingManualAudio"]
                            }
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            onComplete={handleRecordingCompleted}
                            createMeasurementUploadSession={
                              createMeasurementUploadSession
                            }
                            completeMeasurementUploadSession={
                              completeRegionalMeasurementUploadSession
                            }
                            failMeasurementUploadSession={
                              failRegionalMeasurementUploadSession
                            }
                            analytics={analytics}
                            variant={"RecordingManualAudio"}
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/review-metadata"
                  element={
                    <ProtectedRoute allowMissingMetadata>
                      <ConfirmMetadataPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute
                      loadingFallback={<ProfilePageLoadingShell />}
                    >
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/compatibility" element={<CompatibilityPage />} />

                {/* ---------- Fallback ---------- */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            )}
          </Suspense>
        </div>
        {measureOpen ? (
          <>
            <MobileMeasureOverlay
              onClose={closeNavSheets}
              firstRunByVariant={firstRunByVariant}
              isPermitted={isPermitted}
            />
            <div
              className="hidden md:block fixed left-0 right-0 z-40"
              style={{ top: "calc(44px + env(safe-area-inset-top, 0px))" }}
            >
              <button
                type="button"
                className="absolute inset-0 bg-transparent"
                style={{
                  height: "calc(100dvh - 44px - env(safe-area-inset-top, 0px))",
                }}
                aria-label="Close measure"
                onClick={closeNavSheets}
              />
              <div className="relative mx-auto w-full flex justify-center bg-container border-b border-greyscale-3 shadow-lg p-4">
                <MeasurePanel
                  onClose={closeNavSheets}
                  firstRunByVariant={firstRunByVariant}
                  isPermitted={isPermitted}
                />
              </div>
            </div>
          </>
        ) : null}
        {menuOpen && (
          <>
            {/* Mobile overlay */}
            <MobileMenuOverlay
              onClose={closeNavSheets}
              showQuestionnaires={permittedQuestionnaires.length > 0}
              questionnaireSlugs={permittedQuestionnaires}
            />
            {/* Desktop popdown */}
            <div
              className="hidden md:block fixed left-0 right-0 z-40"
              style={{ top: "calc(44px + env(safe-area-inset-top, 0px))" }}
            >
              {/* optional backdrop just behind dropdown */}
              <button
                type="button"
                className="absolute inset-0 bg-transparent"
                style={{
                  height: "calc(100dvh - 44px - env(safe-area-inset-top, 0px))",
                }}
                aria-label="Close menu"
                onClick={closeNavSheets}
              />
              <div className="relative mx-auto w-full flex justify-center bg-container border-b border-greyscale-3  shadow-lg p-4">
                <MenuPanel
                  onClose={closeNavSheets}
                  showQuestionnaires={permittedQuestionnaires.length > 0}
                  questionnaireSlugs={permittedQuestionnaires}
                  dense
                />
              </div>
            </div>
          </>
        )}
        {navVariant === "main" && !isRecordingRoute && !isFormRoute ? (
          <BottomNavBar
            menuOpen={menuOpen}
            measureOpen={measureOpen}
            onToggleMeasure={handleToggleMeasure}
            onToggleMenu={handleToggleMenu}
            onCloseMenu={closeNavSheets}
          />
        ) : null}
      </div>
    </UserClinicMappingProvider>
  );
}
